import { api, Banner, Dialog, Field, Select } from "@getaways/extranet-ui";
import { IconClockPlay } from "@tabler/icons-react";
import { useState } from "react";
import type { Spec } from "./query";

export const PERIODS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last_7_days: "Last 7 days",
  week_to_date: "This week so far",
  last_week: "Last week",
  month_to_date: "This month so far",
  last_month: "Last month",
  crm_period: "This CRM period (21st–20th)",
  last_crm_period: "Last CRM period",
  year_to_date: "This year so far",
};

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export type Cadence = { kind: "daily"; at: string } | { kind: "weekly"; at: string; weekday: number } | { kind: "monthly"; at: string; day: number } | { kind: "crm_period"; at: string };

export function cadenceText(c: Cadence): string {
  if (c.kind === "daily") return `Every day at ${c.at}`;
  if (c.kind === "weekly") return `Every ${WEEKDAYS[c.weekday - 1]} at ${c.at}`;
  if (c.kind === "monthly") return `On day ${c.day} of each month at ${c.at}`;
  return `On the 21st (CRM period) at ${c.at}`;
}

const options = (r: Record<string, string>) => Object.entries(r).map(([value, label]) => ({ value, label }));

/** Saves a table as a scheduled send (docs/36 §9): the same query, a period that moves with time, recipients. */
export function ScheduleDialog({ spec, title, onClose, onSaved }: { spec: Spec; title: string; onClose: () => void; onSaved?: () => void }) {
  const [name, setName] = useState(title);
  const [period, setPeriod] = useState("last_7_days");
  const [kind, setKind] = useState<Cadence["kind"]>("weekly");
  const [at, setAt] = useState("08:00");
  const [weekday, setWeekday] = useState("1");
  const [day, setDay] = useState("1");
  const [format, setFormat] = useState("xlsx");
  const [recipients, setRecipients] = useState("");
  const [chat, setChat] = useState("");
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setError(undefined);
    const cadence: Cadence = kind === "weekly" ? { kind, at, weekday: Number(weekday) } : kind === "monthly" ? { kind, at, day: Number(day) } : { kind, at };
    const { from: _f, to: _t, ...filter } = (spec.filter ?? {}) as Record<string, unknown>;
    try {
      await api("POST", "/v1/schedules", { title: name, spec: { ...spec, filter }, period, format, cadence, recipients: recipients.split(/[\s,;]+/).filter(Boolean), chatWebhook: chat.trim() || null });
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (saved) return <Dialog title="Scheduled" onClose={onClose} actions={<button onClick={onClose}>Close</button>}><p>“{name}” is saved. You find it, and can send it at once, under Schedules.</p></Dialog>;
  return (
    <Dialog title="Schedule this report" description="The same table, sent by email or to a Google Chat space, with your access." onClose={onClose} onSubmit={() => void save()} width={520} actions={<><button type="button" className="ghost" onClick={onClose}>Cancel</button><button type="submit">Save</button></>}>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} /></Field>
      <Field label="Period it covers"><Select value={period} onChange={setPeriod} options={options(PERIODS)} /></Field>
      <div className="grid2">
        <Field label="When"><Select<Cadence["kind"]> value={kind} onChange={setKind} options={[{ value: "daily", label: "Every day" }, { value: "weekly", label: "Every week" }, { value: "monthly", label: "Every month" }, { value: "crm_period", label: "On the 21st" }]} /></Field>
        {kind === "weekly" && <Field label="Day"><Select value={weekday} onChange={setWeekday} options={WEEKDAYS.map((d, i) => ({ value: String(i + 1), label: d }))} /></Field>}
        {kind === "monthly" && <Field label="Day of the month"><input type="number" min={1} max={28} value={day} onChange={(e) => setDay(e.target.value)} /></Field>}
        <Field label="At (Lisbon)"><input type="time" value={at} onChange={(e) => setAt(e.target.value)} required /></Field>
        <Field label="File"><Select value={format} onChange={setFormat} options={[{ value: "xlsx", label: "Excel" }, { value: "csv", label: "CSV" }]} /></Field>
      </div>
      <Field label="Email to" help="Ideastation addresses, separated by commas."><input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="name@ideastation.eu" /></Field>
      <Field label="Google Chat space" help="The space's incoming webhook (Apps & integrations › Webhooks); optional."><input value={chat} onChange={(e) => setChat(e.target.value)} placeholder="https://chat.googleapis.com/v1/spaces/…" /></Field>
    </Dialog>
  );
}

export function ScheduleButton({ spec, title }: { spec: Spec; title: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="ghost" onClick={() => setOpen(true)} title="Send this table on a schedule"><IconClockPlay size={14} /> Schedule</button>
      {open && <ScheduleDialog spec={spec} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}
